import { useState, useEffect } from "react";
import { enrollmentApi } from "../lib";
import { showError } from "../lib/toast";
import {
  Users,
  Loader,
  Search,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Calendar,
  Clock,
} from "lucide-react";

interface Student {
  _id: string;
  user: {
    _id: string;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
    createdAt: string;
  };
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  progress: number;
  status: string;
  enrolledAt: string;
  completedAt?: string;
  lastAccessedAt?: string;
}

interface CourseStats {
  courseId: string;
  courseTitle: string;
}

const InstructorStudentsPage = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<CourseStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    fetchStudents();
  }, [page, selectedCourse]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await enrollmentApi.getInstructorStudents({
        page,
        limit: 15,
        search: searchTerm || undefined,
        courseId: selectedCourse || undefined,
      });

      if (response.success) {
        setStudents(response.data);
        setTotal(response.pagination?.total || 0);

        if (initialLoad) {
          const analyticsRes = await enrollmentApi.getInstructorAnalytics();
          if (analyticsRes.success && analyticsRes.data?.courseStats) {
            setCourses(analyticsRes.data.courseStats);
            setInitialLoad(false);
          }
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch students:", err);
      showError("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchStudents();
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourse(courseId);
    setPage(1);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "active":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
              <Users className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              My Students
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Manage and track your students' progress
          </p>
        </div>

        {/* Filters - Mobile responsive */}
        <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
          <div className="flex flex-col gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-full pl-9 pr-3 py-2 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-sm"
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={selectedCourse}
                onChange={(e) => handleCourseChange(e.target.value)}
                className="flex-1 px-3 py-2 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 md:text-sm"
              >
                <option value="">All Courses</option>
                {courses.map((course) => (
                  <option key={course.courseId} value={course.courseId}>
                    {course.courseTitle}
                  </option>
                ))}
              </select>

              <button
                onClick={handleSearch}
                className="px-4 py-2 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all md:text-sm"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Students Table/Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 text-orange-500 animate-spin" />
            </div>
          ) : students.length === 0 ? (
            <div className="py-12 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-[11px] text-gray-500 md:text-sm">
                No students found
              </p>
              <p className="text-[9px] text-gray-400 mt-1">
                Students will appear here once they enroll
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View - Hidden on mobile */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Course
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Enrolled
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Last Access
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {students.map((student) => (
                      <tr
                        key={student._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center flex-shrink-0">
                              {student.user?.avatar ? (
                                <img
                                  src={student.user.avatar}
                                  alt={student.user?.firstName || "Student"}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-xs font-semibold text-orange-600">
                                  {student.user?.firstName
                                    ?.charAt(0)
                                    .toUpperCase() || "?"}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] font-medium text-gray-900 truncate max-w-[150px]">
                                {student.user?.firstName &&
                                student.user?.lastName
                                  ? `${student.user.firstName} ${student.user.lastName}`
                                  : student.user?.firstName || "Unknown"}
                              </p>
                              <p className="text-[10px] text-gray-500 truncate max-w-[150px]">
                                {student.user?.email || "No email"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {student.course?.thumbnail && (
                              <img
                                src={student.course.thumbnail}
                                alt=""
                                className="w-6 h-6 rounded object-cover flex-shrink-0"
                              />
                            )}
                            <span className="text-[11px] text-gray-600 max-w-[180px] truncate">
                              {student.course?.title || "Unknown Course"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${
                                  student.progress === 100
                                    ? "bg-green-500"
                                    : "bg-gradient-to-r from-orange-500 to-orange-600"
                                }`}
                                style={{ width: `${student.progress}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-medium text-gray-600">
                              {student.progress}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold ${getStatusColor(student.status)}`}
                          >
                            {student.status.charAt(0).toUpperCase() +
                              student.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] text-gray-500 whitespace-nowrap">
                            {formatDate(student.enrolledAt)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] text-gray-500 whitespace-nowrap">
                            {student.lastAccessedAt
                              ? formatDate(student.lastAccessedAt)
                              : "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View - Visible only on mobile */}
              <div className="divide-y divide-gray-100 md:hidden">
                {students.map((student) => (
                  <div
                    key={student._id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center flex-shrink-0">
                        {student.user?.avatar ? (
                          <img
                            src={student.user.avatar}
                            alt={student.user?.firstName || "Student"}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-semibold text-orange-600">
                            {student.user?.firstName?.charAt(0).toUpperCase() ||
                              "?"}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold text-gray-900">
                              {student.user?.firstName && student.user?.lastName
                                ? `${student.user.firstName} ${student.user.lastName}`
                                : student.user?.firstName || "Unknown"}
                            </p>
                            <p className="text-[9px] text-gray-500 truncate">
                              {student.user?.email || "No email"}
                            </p>
                          </div>
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${getStatusColor(student.status)}`}
                          >
                            {student.status.charAt(0).toUpperCase() +
                              student.status.slice(1)}
                          </span>
                        </div>

                        <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-1.5">
                            <BookOpen className="w-3 h-3 text-gray-400" />
                            <span className="text-[9px] text-gray-600 truncate flex-1">
                              {student.course?.title || "Unknown Course"}
                            </span>
                          </div>

                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[8px] text-gray-500">
                              Progress
                            </span>
                            <span className="text-[9px] font-medium text-gray-700">
                              {student.progress}%
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                student.progress === 100
                                  ? "bg-green-500"
                                  : "bg-gradient-to-r from-orange-500 to-orange-600"
                              }`}
                              style={{ width: `${student.progress}%` }}
                            />
                          </div>

                          <div className="flex items-center justify-between text-[9px] text-gray-500">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-2.5 h-2.5" />
                              <span>
                                Enrolled: {formatDate(student.enrolledAt)}
                              </span>
                            </div>
                            {student.lastAccessedAt && (
                              <div className="flex items-center gap-1">
                                <Clock className="w-2.5 h-2.5" />
                                <span>
                                  Last: {formatDate(student.lastAccessedAt)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination - Mobile responsive */}
          {total > 15 && (
            <div className="p-3 border-t border-gray-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[9px] text-gray-500 text-center sm:text-left md:text-xs">
                Showing {students.length} of {total} students
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[10px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Prev
                </button>
                <span className="text-[9px] text-gray-600">
                  Page {page} of {Math.ceil(total / 15)}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * 15 >= total}
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[10px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stats Summary - Optional */}
        {students.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-green-100">
                <Users className="w-3 h-3 text-green-600" />
              </div>
              <p className="text-[10px] font-bold text-gray-900">
                {students.length}
              </p>
              <p className="text-[7px] text-gray-500">Total</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-blue-100">
                <BookOpen className="w-3 h-3 text-blue-600" />
              </div>
              <p className="text-[10px] font-bold text-gray-900">
                {students.filter((s) => s.status === "active").length}
              </p>
              <p className="text-[7px] text-gray-500">Active</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-purple-100">
                <Calendar className="w-3 h-3 text-purple-600" />
              </div>
              <p className="text-[10px] font-bold text-gray-900">
                {students.filter((s) => s.status === "completed").length}
              </p>
              <p className="text-[7px] text-gray-500">Completed</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorStudentsPage;
